import { FormattedMessage } from '@flipster/i18n';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';

export const SparkRewardRankingGraph = ({
  css: cssProp,
  percentiles,
  currentPercentileIndex,
}: {
  css?: Styles;
  percentiles?: number[];
  currentPercentileIndex?: number;
}) => {
  let currentPercentile: number | undefined;
  let nextPercentiles: number[] | undefined;
  if (typeof currentPercentileIndex === 'number' && percentiles) {
    if (currentPercentileIndex !== -1) currentPercentile = percentiles.at(currentPercentileIndex);
    nextPercentiles = percentiles.slice(0, currentPercentileIndex);
  }

  return (
    <div
      className={css(
        center.raw({
          px: [30, 80],
          textAlign: 'center',
          bg: 'background.default.level-0',
          position: 'relative',
          overflow: 'hidden',
        }),
        cssProp,
      )}
      style={{
        ['--bar-top' as never]: nextPercentiles ? `${10 * (nextPercentiles.length + 0.5)}%` : '100%',
        ['--gradient-top' as never]: nextPercentiles?.length === 0 ? 0 : 'var(--bar-top)',
      }}
    >
      <div className={css({ position: 'absolute', inset: 0 })}>
        {nextPercentiles?.map((x, i) => (
          <RemainingPercentileLine
            key={i}
            percentile={x}
            active={nextPercentiles ? i === nextPercentiles.length - 1 : false}
          />
        ))}
      </div>

      <Triangle css={css.raw({ position: 'relative', flex: 1 })} nextPercentiles={nextPercentiles} />

      {nextPercentiles?.length === 0 && (
        <BoltIcon
          className={css({
            position: 'absolute',
            bottom: '15%',
            left: '50%',
            transform: 'translateX(-50%)',
            fill: 'inverse.white',
          })}
          size='50%'
        />
      )}

      <MyPercentileLine
        css={css.raw({
          position: 'absolute',
          left: 0,
          right: 0,
          top: 'var(--bar-top)',
        })}
        text={
          currentPercentile
            ? `Top ${currentPercentile}%`
            : percentiles?.at(-1)
              ? `Bottom ${percentiles.at(-1)}%`
              : undefined
        }
        contentPosition={nextPercentiles?.length === 0 ? 'end' : 'center'}
      />
    </div>
  );
};

const RemainingPercentileLine = ({ percentile, active }: { percentile: number; active?: boolean }) => {
  return (
    <div
      className={hstack({
        pl: '50%',
        height: '10%',
        opacity: active ? 1 : 0.2,
      })}
    >
      <div
        className={css({
          flex: 1,
          borderBottomWidth: 1,
          borderBottomStyle: 'dashed',
          borderBottomColor: 'inverse.white',
        })}
      />
      <div className={css({ textStyle: 'Text/XXS/Strong', color: 'inverse.white', px: 8 })}>
        <FormattedMessage
          id='Autogen_common.r251126.s_Top_percentile'
          values={{
            percentile: percentile,
          }}
        />
      </div>
    </div>
  );
};

const MyPercentileLine = ({
  css: cssProp,
  text,
  contentPosition,
}: {
  css?: Styles;
  text?: string;
  contentPosition?: 'center' | 'end';
}) => {
  return (
    <div
      className={css(
        hstack.raw({
          justify: contentPosition === 'end' ? 'flex-end' : 'center',
          borderTopColor: 'content.default.level-5',
          borderTopWidth: 2,
          borderTopStyle: 'solid',
          pt: 8,
        }),
        cssProp,
      )}
    >
      <div
        className={css({
          textStyle: 'Text/XS/Strong',
          color: 'inverse.black',
          bg: 'inverse.white',
          rounded: 10,
          py: 2,
          px: 6,
        })}
      >
        ME
      </div>
      <div className={css({ textStyle: 'Text/M/Strong', color: 'inverse.white' })}>{text}</div>
    </div>
  );
};

const Triangle = ({ css: cssProp, nextPercentiles }: { css?: Styles; nextPercentiles?: number[] }) => {
  return (
    <svg className={css(cssProp)} viewBox='0 0 240 200' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <mask id='triangle-masking' style={{ maskType: 'alpha' }} maskUnits='userSpaceOnUse'>
        <path
          fill='black'
          d='M114.664 2.94117C117.036 -0.980395 122.964 -0.980389 125.336 2.94118L239.165 191.176C241.537 195.098 238.573 200 233.83 200H6.17039C1.42749 200 -1.53681 195.098 0.834638 191.176L114.664 2.94117Z'
        />
      </mask>

      <g mask='url(#triangle-masking)'>
        {/* background masking */}
        <rect width='100%' height='100%' className={css({ fill: 'background.default.level-3' })} />

        {/* zebra masking */}
        {nextPercentiles?.map((_, i) =>
          i % 2 === 0 ? (
            <rect key={i} width='100%' height='10%' fill='white' opacity='0.05' y={`${10 * (i + 1)}%`} />
          ) : null,
        )}

        {/* gradient masking */}
        <rect
          width='100%'
          height='100%'
          style={{ transform: 'translateY(var(--gradient-top))' }}
          fill={nextPercentiles?.length === 0 ? 'url(#gradient-vip)' : 'url(#gradient-promotion)'}
        />
      </g>

      <defs>
        {/* Gradients/Promotion */}
        <linearGradient id='gradient-promotion' gradientUnits='userSpaceOnUse'>
          <stop stopColor='#3326C8' />
          <stop offset='0.5' stopColor='#3326C8' />
          <stop offset='1' stopColor='#7815DB' />
        </linearGradient>

        {/* Gradients/Vip */}
        <linearGradient id='gradient-vip' gradientUnits='userSpaceOnUse' x1='-30' y1='0' x2='157.169' y2='277.973'>
          <stop stopColor='#E4AF5B' />
          <stop offset='0.1' stopColor='#FFCC7B' />
          <stop offset='0.3' stopColor='#FFF6E9' />
          <stop offset='0.4' stopColor='#FFF6E9' />
          <stop offset='0.595' stopColor='#FFCC7B' />
          <stop offset='1' stopColor='#E4AF5B' />
        </linearGradient>
      </defs>
    </svg>
  );
};
